import CheckIcon from "@mui/icons-material/Check";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";

const REQUIRED_COLUMNS = [
  "Número",
  "Punto de venta",
  "Tipo de documento",
  "Fecha de inicio",
  "Fecha de cierre",
  "Descripción",
  "Cantidad",
  "Precio total",
];

function ResultBadge({ success }) {
  return (
    <Box
      role="status"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.7,
        alignSelf: "flex-start",
        px: 1.4,
        py: 0.65,
        borderRadius: "999px",
        bgcolor: success ? "success.main" : "rgba(237, 108, 2, 0.14)",
        border: "1px solid",
        borderColor: success ? "success.main" : "warning.main",
      }}
    >
      {success ? (
        <CheckIcon sx={{ color: "logoblanco", fontSize: 18 }} />
      ) : (
        <ErrorOutlineIcon sx={{ color: "warning.dark", fontSize: 18 }} />
      )}
      <Typography
        sx={{
          color: success ? "logoblanco" : "warning.dark",
          fontSize: 14,
          fontWeight: 800,
        }}
      >
        {success ? "Verificación correcta" : "Errores encontrados"}
      </Typography>
    </Box>
  );
}

function InitialState() {
  return (
    <Stack
      spacing={1.5}
      sx={{ height: "100%", minHeight: { xs: 300, md: 0 }, alignItems: "center", justifyContent: "center" }}
    >
      <Typography
        component="h2"
        sx={{ color: "primary.900", fontSize: 18, fontWeight: 800, textAlign: "center" }}
      >
        Importá tu archivo de ventas
      </Typography>
      <Typography
        sx={{ maxWidth: 560, color: "primary.900", fontSize: 13, fontWeight: 600, textAlign: "center" }}
      >
        Seleccioná o arrastrá un archivo Excel para verificar su estructura, sus
        comprobantes y los productos vendidos.
      </Typography>
      <Typography
        sx={{ maxWidth: 760, color: "neutral.700", fontSize: 11, fontWeight: 600, textAlign: "center" }}
      >
        Columnas necesarias: {REQUIRED_COLUMNS.join(" · ")}
      </Typography>
    </Stack>
  );
}

function LoadingState() {
  return (
    <Stack
      spacing={1.5}
      sx={{ height: "100%", minHeight: { xs: 300, md: 0 }, alignItems: "center", justifyContent: "center" }}
    >
      <CircularProgress size={34} />
      <Typography sx={{ color: "primary.900", fontSize: 16, fontWeight: 800 }}>
        Verificando archivo…
      </Typography>
      <Typography sx={{ color: "neutral.700", fontSize: 12, fontWeight: 600 }}>
        Estamos validando la estructura, las fechas y los comprobantes.
      </Typography>
    </Stack>
  );
}

function SuccessState({ draft }) {
  const checks = [
    "El archivo es un Excel válido",
    "Se encontraron las columnas obligatorias",
    `Se pudieron leer ${Number(draft.processedRows).toLocaleString("es-AR")} filas`,
    "No se encontraron cantidades inválidas",
    "No se encontraron fechas inválidas",
    "El archivo completo no fue importado anteriormente",
  ];

  return (
    <Stack spacing={1.5}>
      <Box>
        <Typography component="h2" sx={{ color: "primary.900", fontSize: 17, fontWeight: 800 }}>
          Paso 1: Archivo
        </Typography>
        <Typography sx={{ color: "neutral.700", fontSize: 12, fontWeight: 600 }}>
          Verificación del archivo
        </Typography>
      </Box>

      <Stack
        spacing={0.85}
        sx={{
          p: 1.5,
          borderRadius: "22px",
          bgcolor: "secondary.200",
          boxShadow: "inset 0px 0px 3px rgba(117, 117, 138, 0.28)",
        }}
      >
        {checks.map((label) => (
          <Stack key={label} direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Box
              sx={{
                width: 20,
                height: 20,
                flexShrink: 0,
                display: "grid",
                placeItems: "center",
                borderRadius: "50%",
                bgcolor: "neutral.50",
                border: "1px solid",
                borderColor: "success.main",
              }}
            >
              <CheckIcon sx={{ color: "success.main", fontSize: 15 }} />
            </Box>
            <Typography sx={{ color: "primary.900", fontSize: 13, fontWeight: 650 }}>
              {label}
            </Typography>
          </Stack>
        ))}
      </Stack>

      <Typography sx={{ color: "neutral.700", fontSize: 11, fontWeight: 700 }}>
        Resultado:
      </Typography>
      <ResultBadge success />
    </Stack>
  );
}

function ErrorState({ result }) {
  const errors = Array.isArray(result?.errors) ? result.errors : [];
  const blockingCount = Number(result?.conflictCount) || errors.length || 1;
  const blockingLabel = `Se encontraron ${blockingCount.toLocaleString("es-AR")} ${
    blockingCount === 1 ? "error bloqueante" : "errores bloqueantes"
  }.`;

  function receiptLabel(receipt) {
    if (!receipt) return "";
    return [
      receipt.documentType,
      receipt.pointOfSale ? `Punto de venta ${receipt.pointOfSale}` : "",
      receipt.number ? `Número ${receipt.number}` : "",
    ]
      .filter(Boolean)
      .join(" · ");
  }

  return (
    <Stack spacing={1.5} sx={{ height: "100%", minHeight: 0, overflow: { md: "hidden" } }}>
      <Box>
        <Typography component="h2" sx={{ color: "primary.900", fontSize: 17, fontWeight: 800 }}>
          Paso 1: Archivo
        </Typography>
        <Typography sx={{ color: "neutral.700", fontSize: 12, fontWeight: 600 }}>
          Verificación del archivo
        </Typography>
      </Box>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{ alignItems: { xs: "flex-start", sm: "center" }, justifyContent: "space-between" }}
      >
        <Box>
          <Typography sx={{ color: "neutral.700", fontSize: 11, fontWeight: 700, mb: 0.6 }}>
            Resultado:
          </Typography>
          <ResultBadge success={false} />
        </Box>
        <Box>
          <Typography sx={{ color: "neutral.700", fontSize: 12, fontWeight: 700 }}>
            {blockingLabel}
          </Typography>
          <Typography sx={{ color: "neutral.700", fontSize: 12, fontWeight: 700 }}>
            Corregilos y reemplazá el archivo para continuar.
          </Typography>
        </Box>
      </Stack>

      <Box
        role="alert"
        sx={{ px: 1.5, py: 1.1, borderRadius: "14px", bgcolor: "secondary.700" }}
      >
        <Typography sx={{ color: "logoblanco", fontSize: 12, fontWeight: 700 }}>
          {result?.message || "No se pudo verificar el archivo de ventas."}
        </Typography>
        {result?.integratedImportId ? (
          <Typography sx={{ color: "logoblanco", fontSize: 11, fontWeight: 600, opacity: 0.95 }}>
            Importación relacionada: #{result.integratedImportId}
          </Typography>
        ) : null}
      </Box>

      {errors.length ? (
        <Box
          role="table"
          aria-label="Errores del archivo"
          sx={{ flex: 1, minHeight: 0, borderRadius: "18px", overflow: "auto", bgcolor: "neutral.50" }}
        >
          <Box sx={{ minWidth: 560 }}>
            <Box
              role="row"
              sx={{
                display: "grid",
                gridTemplateColumns: "90px minmax(130px, .8fr) minmax(280px, 2fr)",
                gap: 1,
                px: 2,
                py: 1.25,
                bgcolor: "neutral.600",
                position: "sticky",
                top: 0,
                zIndex: 1,
              }}
            >
              {["Fila", "Columna", "Problema"].map((heading) => (
                <Typography
                  key={heading}
                  role="columnheader"
                  sx={{ color: "logoblanco", fontSize: 13, fontWeight: 750 }}
                >
                  {heading}
                </Typography>
              ))}
            </Box>
            {errors.map((error) => (
              <Box
                key={error.id}
                role="row"
                sx={{
                  display: "grid",
                  gridTemplateColumns: "90px minmax(130px, .8fr) minmax(280px, 2fr)",
                  gap: 1,
                  px: 2,
                  py: 1.15,
                  borderBottom: "1px solid rgba(0,0,0,.06)",
                }}
              >
                <Typography role="cell" sx={{ color: "primary.900", fontSize: 13, fontWeight: 600 }}>
                  {error.row ?? "General"}
                </Typography>
                <Typography role="cell" sx={{ color: "primary.900", fontSize: 13, fontWeight: 600 }}>
                  {error.column || "—"}
                </Typography>
                <Box role="cell">
                  <Typography sx={{ color: "primary.900", fontSize: 13, fontWeight: 600 }}>
                    {error.message}
                    {error.integratedImportId ? ` (importación #${error.integratedImportId})` : ""}
                  </Typography>
                  {receiptLabel(error.receipt) ? (
                    <Typography sx={{ color: "neutral.700", fontSize: 11, fontWeight: 650 }}>
                      Comprobante: {receiptLabel(error.receipt)}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}
    </Stack>
  );
}

export default function SalesImportStepOne({ status = "idle", draft, error }) {
  if (status === "uploading") return <LoadingState />;
  if (status === "success" && draft) return <SuccessState draft={draft} />;
  if (status === "error" && error) return <ErrorState result={error} />;
  return <InitialState />;
}
